import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code2,
  Link as LinkIcon,
} from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const toggleCls = (active) =>
  active ? 'bg-primary text-primary-foreground border-primary' : '';

function ToolbarButton({ pressed, onPress, icon: Icon, label }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Toggle
          size="sm"
          variant="outline"
          pressed={pressed}
          onPressedChange={onPress}
          className={toggleCls(pressed)}
        >
          <Icon className="h-4 w-4" />
        </Toggle>
      </TooltipTrigger>
      <TooltipContent><p>{label}</p></TooltipContent>
    </Tooltip>
  );
}

const NoteEditor = forwardRef(function NoteEditor({ content, onDirtyChange, placeholder = 'Start writing...', editable = true, isDirty = false }, ref) {
  const [activeStates, setActiveStates] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    heading1: false,
    heading2: false,
    heading3: false,
    bulletList: false,
    orderedList: false,
    blockquote: false,
    codeBlock: false,
    link: false,
  });

  // Track the "original" content the editor was initialized/loaded with
  const originalContentRef = useRef(content || '');
  const lastDirtyRef = useRef(false);

  const updateActiveStates = (editor) => {
    setActiveStates({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      strike: editor.isActive('strike'),
      heading1: editor.isActive('heading', { level: 1 }),
      heading2: editor.isActive('heading', { level: 2 }),
      heading3: editor.isActive('heading', { level: 3 }),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      blockquote: editor.isActive('blockquote'),
      codeBlock: editor.isActive('codeBlock'),
      link: editor.isActive('link'),
    });
  };

  const checkDirty = (editor) => {
    const currentHTML = editor.getHTML();
    const dirty = currentHTML !== originalContentRef.current;
    if (dirty !== lastDirtyRef.current) {
      lastDirtyRef.current = dirty;
      onDirtyChange?.(dirty);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    editable,
    onCreate: ({ editor }) => {
      originalContentRef.current = editor.getHTML();
    },
    onUpdate: ({ editor }) => {
      updateActiveStates(editor);
      checkDirty(editor);
    },
    onSelectionUpdate: ({ editor }) => updateActiveStates(editor),
    onTransaction: ({ editor }) => updateActiveStates(editor),
    editorProps: {
      attributes: {
        class: 'tiptap-notes',
      },
    },
  });

  // Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() ?? '',
    isEmpty: () => editor?.isEmpty ?? true,
    isDirty: () => {
      if (!editor) return false;
      return editor.getHTML() !== originalContentRef.current;
    },
    resetContent: () => {
      if (!editor) return;
      editor.commands.setContent(originalContentRef.current || '');
      lastDirtyRef.current = false;
      onDirtyChange?.(false);
    },
    /** Update baseline without changing editor content. Recalculates dirty state. */
    updateBaseline: (newBaseline) => {
      if (!editor) return;
      originalContentRef.current = newBaseline || '';
      checkDirty(editor);
    },
  }), [editor, onDirtyChange]);

  // Sync content prop changes (external updates like selecting a different note)
  // Guard: skip if user has in-progress edits (protects against external broadcasts)
  useEffect(() => {
    if (!editor) return;
    if (isDirty) return; // Don't overwrite user's in-progress edits
    const incoming = content || '';
    if (incoming !== originalContentRef.current) {
      editor.commands.setContent(incoming);
      originalContentRef.current = editor.getHTML();
      lastDirtyRef.current = false;
      onDirtyChange?.(false);
    }
  }, [content, editor, isDirty]);

  // Sync editable prop
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  if (!editor) return null;

  const addLink = () => {
    if (activeStates.link) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {/* Toolbar */}
        {editable && (
          <div className="flex gap-1 px-4 py-2 border-b border-border bg-muted/30 flex-wrap sticky top-0 z-10">
            <ToolbarButton pressed={activeStates.bold} onPress={() => editor.chain().focus().toggleBold().run()} icon={Bold} label="Bold (Ctrl+B)" />
            <ToolbarButton pressed={activeStates.italic} onPress={() => editor.chain().focus().toggleItalic().run()} icon={Italic} label="Italic (Ctrl+I)" />
            <ToolbarButton pressed={activeStates.underline} onPress={() => editor.chain().focus().toggleUnderline().run()} icon={UnderlineIcon} label="Underline (Ctrl+U)" />
            <ToolbarButton pressed={activeStates.strike} onPress={() => editor.chain().focus().toggleStrike().run()} icon={Strikethrough} label="Strikethrough" />

            <Separator orientation="vertical" className="mx-1 h-8" />

            <ToolbarButton pressed={activeStates.heading1} onPress={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} icon={Heading1} label="Heading 1" />
            <ToolbarButton pressed={activeStates.heading2} onPress={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} icon={Heading2} label="Heading 2" />
            <ToolbarButton pressed={activeStates.heading3} onPress={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} icon={Heading3} label="Heading 3" />

            <Separator orientation="vertical" className="mx-1 h-8" />

            <ToolbarButton pressed={activeStates.bulletList} onPress={() => editor.chain().focus().toggleBulletList().run()} icon={List} label="Bullet List" />
            <ToolbarButton pressed={activeStates.orderedList} onPress={() => editor.chain().focus().toggleOrderedList().run()} icon={ListOrdered} label="Numbered List" />

            <Separator orientation="vertical" className="mx-1 h-8" />

            <ToolbarButton pressed={activeStates.blockquote} onPress={() => editor.chain().focus().toggleBlockquote().run()} icon={Quote} label="Blockquote" />
            <ToolbarButton pressed={activeStates.codeBlock} onPress={() => editor.chain().focus().toggleCodeBlock().run()} icon={Code2} label="Code Block" />

            <Separator orientation="vertical" className="mx-1 h-8" />

            <ToolbarButton pressed={activeStates.link} onPress={addLink} icon={LinkIcon} label="Link" />
          </div>
        )}

        {/* Editor Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <style>{`
            .tiptap-notes {
              padding: 1rem 2rem;
              min-height: 100%;
              outline: none;
              font-size: 1rem;
              line-height: 1.75;
            }
            .tiptap-notes p {
              margin: 0.5em 0;
            }
            .tiptap-notes h1 {
              font-size: 1.5rem;
              font-weight: 700;
              margin: 1em 0 0.5em;
            }
            .tiptap-notes h2 {
              font-size: 1.25rem;
              font-weight: 600;
              margin: 0.75em 0 0.5em;
            }
            .tiptap-notes h3 {
              font-size: 1.1rem;
              font-weight: 600;
              margin: 0.75em 0 0.5em;
            }
            .tiptap-notes ul,
            .tiptap-notes ol {
              padding-left: 1.5em;
              margin: 0.5em 0;
            }
            .tiptap-notes ul {
              list-style-type: disc;
            }
            .tiptap-notes ol {
              list-style-type: decimal;
            }
            .tiptap-notes blockquote {
              border-left: 3px solid hsl(var(--border));
              padding-left: 1em;
              margin: 0.5em 0;
              color: hsl(var(--muted-foreground));
            }
            .tiptap-notes pre {
              background: hsl(var(--muted));
              border-radius: 0.375rem;
              padding: 0.75em 1em;
              margin: 0.5em 0;
              overflow-x: auto;
            }
            .tiptap-notes code {
              font-family: ui-monospace, monospace;
              font-size: 0.9em;
            }
            .tiptap-notes a {
              color: hsl(var(--primary));
              text-decoration: underline;
            }
            .tiptap-notes strong {
              font-weight: bold;
            }
            .tiptap-notes em {
              font-style: italic;
            }
            .tiptap-notes u {
              text-decoration: underline;
            }
            .tiptap-notes s {
              text-decoration: line-through;
            }
            .tiptap-notes hr {
              border: none;
              border-top: 1px solid hsl(var(--border));
              margin: 1em 0;
            }
            .tiptap-notes .is-editor-empty:first-child::before {
              color: hsl(var(--muted-foreground) / 0.4);
              content: attr(data-placeholder);
              float: left;
              height: 0;
              pointer-events: none;
            }
          `}</style>
          <EditorContent editor={editor} />
        </div>
      </div>
    </TooltipProvider>
  );
});

export default NoteEditor;
